/**
 * "Where models come from": each model supplier and whether this install can
 * use it, on the Model catalog page above the catalog table.
 *
 * The catalog hides, and the model pickers leave out, models no ready
 * supplier lists, so this card is where an operator finds out why a set-up
 * supplier's models are missing: a key the supplier turned down, or a list
 * check that failed.
 *
 * **Only suppliers this install is set up for** (#2880). A supplier the
 * server reports as `noKey` is left out, because most installs use one
 * supplier and a row per unused one buried it. "Set up" is the server's own
 * rule, so OttoCloud under organization scope, which has an address and no
 * install key, still shows. With nothing set up, one line says where to start.
 * It lives beside the catalog because that is what it describes; the
 * Assignments page links here instead of repeating it.
 *
 * **OttoCloud first, the rest folded.** An install that signed up with
 * OttoCloud made its choice during setup, so when OttoCloud is ready the
 * other set-up suppliers show by name only, under a closed "Other suppliers"
 * line. Without OttoCloud they show open.
 *
 * Its own file because it is a self-contained card with its own state (the
 * Refresh now buttons); the page only passes it the supplier list.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { adminScheduledJobsAPI, type ModelSupplierStatus } from '@/lib/api'
// The supplier whose readiness folds the others away.
import { HOSTED_SUPPLIER } from '@/lib/agentModelSpec'

export function ModelSuppliersCard({
  suppliers,
  onListed,
}: {
  suppliers: readonly ModelSupplierStatus[]
  /** Called when a Refresh now run finishes, so the page can reload. */
  onListed: () => void
}) {
  const setUp = suppliers.filter(s => s.state !== 'noKey')
  const hosted = setUp.find(s => s.supplier === HOSTED_SUPPLIER)
  const foldOthers = hosted?.state === 'ready'
  const lead = foldOthers && hosted ? [hosted] : setUp
  const others = foldOthers ? setUp.filter(s => s.supplier !== HOSTED_SUPPLIER) : []
  const [othersOpen, setOthersOpen] = useState(false)

  if (suppliers.length === 0) return null
  return (
    <Card>
      <CardHeader>
        <CardTitle>Where models come from</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <p className="text-xs text-muted-foreground">
          This list is refreshed daily. To get the current list, press Refresh now, or run its job from{' '}
          <Link to="/admin/scheduled-jobs" className="underline-offset-4 hover:underline text-primary">
            Scheduled Jobs
          </Link>
          .
        </p>
        {setUp.length === 0 ? (
          <p className="text-sm pt-1">
            No model supplier is set up yet. Add one to this install's settings file and restart OttoTester.
            The Environment variables help page lists what each supplier needs.
          </p>
        ) : (
          <div className="divide-y">
            {lead.map(s => <SupplierRow key={s.supplier} supplier={s} onListed={onListed} />)}
          </div>
        )}
        {others.length > 0 && (
          <div className="border-t pt-2">
            <button
              type="button"
              onClick={() => setOthersOpen(o => !o)}
              aria-expanded={othersOpen}
              aria-controls="other-suppliers"
              className="flex flex-wrap items-baseline gap-2 text-left text-sm rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              <ChevronRight
                aria-hidden="true"
                className={`h-3.5 w-3.5 self-center text-muted-foreground transition-transform ${othersOpen ? 'rotate-90' : ''}`}
              />
              <span className="font-semibold">Other suppliers</span>{' '}
              <span className="text-xs text-muted-foreground">{others.map(s => s.name).join(' · ')}</span>
            </button>
            {othersOpen && (
              <div id="other-suppliers" className="divide-y mt-1">
                {others.map(s => <SupplierRow key={s.supplier} supplier={s} onListed={onListed} />)}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/** How often, and for how long, Refresh now checks on its run. A listing
 * takes seconds; the job's own limit is a few minutes. */
const POLL_MS = 2000
const POLL_LIMIT_MS = 3 * 60_000

function SupplierRow({ supplier, onListed }: { supplier: ModelSupplierStatus; onListed: () => void }) {
  const [running, setRunning] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const canList = supplier.state !== 'noKey' && Boolean(supplier.listingJobId)
  // Whether the card is still on screen, so a run that ends after the admin
  // leaves does not update or reload anything. Set on every mount, not just
  // cleared on unmount: React's development mode mounts each component twice,
  // and a flag only ever cleared stayed false from the start, so the button
  // never saw its run finish and stuck on "Checking…".
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  async function refreshNow(): Promise<void> {
    const jobId = supplier.listingJobId
    if (!jobId) return
    setRunning(true)
    setMessage(null)
    try {
      const { runId } = await adminScheduledJobsAPI.runNow(jobId)
      if (!alive.current) return
      setMessage('Checking the list…')
      // The run returns at once and finishes later, so wait for it before
      // reloading; reloading straight away would show the old list.
      const deadline = Date.now() + POLL_LIMIT_MS
      while (alive.current && Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, POLL_MS))
        if (!alive.current) return
        const run = (await adminScheduledJobsAPI.get(jobId)).recentRuns.find(r => r.runId === runId)
        if (!alive.current) return
        if (run?.completedAt) {
          setMessage(run.status === 'succeeded' ? 'Updated.' : `The list check failed: ${run.errorSummary ?? 'see Scheduled Jobs'}.`)
          onListed()
          return
        }
      }
      if (alive.current) setMessage('Still running. Check Scheduled Jobs for the result.')
    } catch (e) {
      if (alive.current) setMessage(e instanceof Error ? e.message : 'Could not start the listing.')
    } finally {
      if (alive.current) setRunning(false)
    }
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[7.5rem_1fr_auto] gap-x-3 gap-y-1 py-2.5 items-start">
      <span className="font-semibold text-sm">{supplier.name}</span>
      <div className="min-w-0 text-xs space-y-1">
        <SupplierState supplier={supplier} />
        {message && <p className="text-muted-foreground" role="status">{message}</p>}
      </div>
      {canList ? (
        <Button size="sm" variant="outline" onClick={() => void refreshNow()} disabled={running}>
          {running ? 'Checking…' : 'Refresh now'}
        </Button>
      ) : <span />}
    </div>
  )
}

function SupplierState({ supplier }: { supplier: ModelSupplierStatus }) {
  const when = supplier.listedAt ? formatListedAt(supplier.listedAt) : null
  switch (supplier.state) {
    case 'ready':
      return (
        <p>
          <Badge variant="secondary" className="text-[10px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">Ready</Badge>{' '}
          <span className="text-muted-foreground">
            {supplier.listedCount} models{when ? ` · listed ${when}` : ''}
          </span>
        </p>
      )
    case 'keyRejected':
      return (
        <>
          <p><Badge variant="destructive" className="text-[10px]">Key rejected</Badge></p>
          <p>
            {supplier.name} turned down the key in <code>{supplier.keySetting}</code>. Put a working key there and
            restart, then press Refresh now. Until then, no {supplier.name} model can be picked.
          </p>
        </>
      )
    case 'failed':
      return (
        <p>
          <Badge variant="warning" className="text-[10px]">Couldn't reach</Badge>{' '}
          <span className="text-muted-foreground">
            The last list check failed, so its models are offered from the list{when ? ` of ${when}` : ' it had before'}.
          </span>
        </p>
      )
    case 'notListedYet':
      return (
        <p>
          <Badge variant="outline" className="text-[10px]">Not listed yet</Badge>{' '}
          <span className="text-muted-foreground">Its models are all offered until its first list check finishes.</span>
        </p>
      )
    default:
      // `noKey`: the card leaves those suppliers out (#2880), so no row gets here.
      return null
  }
}

/** "today at 6:00 AM", or a short date for anything older. */
function formatListedAt(iso: string): string {
  const d = new Date(iso)
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return d.toDateString() === new Date().toDateString()
    ? `today at ${time}`
    : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${time}`
}
